import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api, buildQuery } from '@/api/client'
import type { Market } from '@/api/types'
import { ErrorBox, Segmented } from '@/components/ui/Primitives'
import { formatNumber, relativeTime } from '@/lib/utils'

type Scope = Market | 'ALL'

/** Open IT postings by the experience they ask for, and the skills in demand. */
export function HiringInsights() {
  const [scope, setScope] = useState<Scope>('IN')
  const market = scope === 'ALL' ? undefined : scope
  const insights = useQuery({
    queryKey: ['insights', scope],
    queryFn: () => api.insights(market),
    refetchInterval: 300_000,
  })
  const d = insights.data
  const bands = d?.experience_bands.filter((b) => b.key !== 'none') ?? []
  const notStated = d?.experience_bands.find((b) => b.key === 'none')
  const bandMax = Math.max(1, ...bands.map((b) => b.jobs))
  const skillMax = Math.max(1, ...(d?.top_skills.map((s) => s.count) ?? []))
  const jobsLink = (params: Record<string, string | undefined>) => `/jobs${buildQuery({ market, ...params })}`

  return (
    <section className="card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold">Hiring insights</h2>
          <p className="text-[12.5px] text-ink-muted">
            Open IT jobs by the experience they ask for, and the skills in demand today.
            {d && ` Updated ${relativeTime(d.generated_at)}.`}
          </p>
        </div>
        <Segmented<Scope>
          size="sm"
          value={scope}
          onChange={setScope}
          options={[
            { value: 'IN', label: '🇮🇳 India' },
            { value: 'US', label: '🇺🇸 USA' },
            { value: 'ALL', label: 'All' },
          ]}
        />
      </div>

      {insights.isError && <ErrorBox error={insights.error} onRetry={() => void insights.refetch()} />}
      {insights.isPending && <p className="py-8 text-center text-[13px] text-ink-muted">Counting open jobs…</p>}

      {d && (
        <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <h3 className="label-eyebrow mb-3">Openings by experience</h3>
            <ul className="space-y-3.5">
              {bands.map((band) => (
                <li key={band.key}>
                  <div className="mb-1 flex items-baseline justify-between text-[13.5px]">
                    <Link to={jobsLink({ exp: band.key })} className="font-medium hover:underline">
                      {band.label}
                    </Link>
                    <span className="tabular-nums">
                      <b className="font-semibold">{formatNumber(band.jobs)}</b>
                      <span className="ml-1.5 text-[12px] text-ink-faint">
                        {d.jobs_with_experience ? Math.round((band.jobs / d.jobs_with_experience) * 100) : 0}%
                      </span>
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-muted">
                    <div className="h-full rounded-full bg-ink/70" style={{ width: `${(band.jobs / bandMax) * 100}%` }} />
                  </div>
                  {band.top_skills.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {band.top_skills.slice(0, 6).map((s) => (
                        <Link
                          key={s.value}
                          to={jobsLink({ exp: band.key, skills: s.value })}
                          className="rounded-pill border border-line px-2 py-0.5 text-[11.5px] text-ink-muted hover:border-line-strong hover:text-ink"
                        >
                          {s.value} <span className="tabular-nums text-ink-faint">{formatNumber(s.count)}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[12px] text-ink-faint">
              Bands use the minimum experience a posting asks for (5 – 10 yrs = 5 to 9 years minimum).{' '}
              {formatNumber(d.jobs_with_experience)} of {formatNumber(d.jobs_total)} open jobs state it
              {notStated ? `; ${formatNumber(notStated.jobs)} don't` : ''}.
            </p>

            <h3 className="label-eyebrow mb-2 mt-6">Top hiring companies</h3>
            <ol className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
              {d.top_companies.map((c, i) => (
                <li key={c.company_id} className="flex justify-between gap-2">
                  <Link to={jobsLink({ company_id: String(c.company_id) })} className="truncate hover:underline">
                    <span className="mr-1.5 tabular-nums text-ink-faint">{i + 1}.</span>
                    {c.name}
                  </Link>
                  <span className="shrink-0 tabular-nums text-ink-muted">{formatNumber(c.jobs)}</span>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h3 className="label-eyebrow mb-3">Top skills required</h3>
            <ol className="space-y-2">
              {d.top_skills.map((s, i) => (
                <li key={s.value}>
                  <div className="mb-0.5 flex justify-between text-[13px]">
                    <Link to={jobsLink({ skills: s.value })} className="hover:underline">
                      <span className="mr-1.5 tabular-nums text-ink-faint">{i + 1}.</span>
                      {s.value}
                    </Link>
                    <span className="tabular-nums text-ink-muted">
                      {formatNumber(s.count)}
                      <span className="ml-1.5 text-[11.5px] text-ink-faint">
                        {d.jobs_with_skills ? Math.round((s.count / d.jobs_with_skills) * 100) : 0}%
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted">
                    <div className="h-full rounded-full bg-[#1f7a4d]/70" style={{ width: `${(s.count / skillMax) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-3 text-[12px] text-ink-faint">% of the {formatNumber(d.jobs_with_skills)} open jobs that list skills.</p>
          </div>
        </div>
      )}
    </section>
  )
}
